import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FileText, Building2, CreditCard, Trash2, Clock, CheckCircle2, XCircle, Download, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MONTH_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function fmtDate(d) {
  if (!d) return '-';
  const dt = new Date(d);
  return `${dt.getDate()} ${MONTH_SHORT[dt.getMonth()]} ${dt.getFullYear()}`;
}

function StatusBadge({ status }) {
  if (status === 'completed') return <span className="flex items-center gap-1 text-[10px] font-medium text-[#4A7A5D] bg-[#4A7A5D]/10 px-2 py-0.5 rounded"><CheckCircle2 size={10} /> Completed</span>;
  if (status === 'failed') return <span className="flex items-center gap-1 text-[10px] font-medium text-red-600 bg-red-50 px-2 py-0.5 rounded"><XCircle size={10} /> Failed</span>;
  if (status === 'processing') return <span className="flex items-center gap-1 text-[10px] font-medium text-orange-600 bg-orange-50 px-2 py-0.5 rounded"><Clock size={10} /> Processing</span>;
  return <span className="text-[10px] font-medium text-[#5C5C55] bg-[#E8E6DE] px-2 py-0.5 rounded">{status || 'Unknown'}</span>;
}

const CC_SOURCES = ['axis', 'amex', 'hdfc cc', 'icici cc'];
function isCreditCard(stmt) {
  const src = (stmt.source || '').toLowerCase();
  const stype = (stmt.statement_type || '').toLowerCase();
  if (stype.includes('credit') || stype.includes('card')) return true;
  return CC_SOURCES.some(cc => src.includes(cc));
}

export default function BusinessUploadHistoryPage() {
  const [statements, setStatements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [topTab, setTopTab] = useState('bank');
  const [activeSource, setActiveSource] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null, name: '' });
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 12;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/business/statements`, { withCredentials: true });
      setStatements(data);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/statements/${id}`, { withCredentials: true });
      toast.success('Statement deleted');
      setDeleteDialog({ open: false, id: null, name: '' });
      fetchData();
    } catch (err) { toast.error('Failed to delete statement'); }
  };

  const isBankTab = topTab === 'bank';

  const bankStatements = statements.filter(s => s.source && !isCreditCard(s));
  const cardStatements = statements.filter(s => isCreditCard(s));
  const activeStatements = isBankTab ? bankStatements : cardStatements;

  const normSource = (raw) => {
    const s = (raw || '').toUpperCase().trim();
    if (s.includes('ICICI')) return 'ICICI Business';
    if (s.includes('HDFC')) return 'HDFC Business';
    if (s.includes('AXIS')) return 'Axis Business';
    return raw?.trim() || 'Unknown';
  };

  const grouped = {};
  activeStatements.forEach(s => {
    const key = normSource(s.source);
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(s);
  });
  Object.values(grouped).forEach(arr =>
    arr.sort((a, b) => (b.statement_date || '').localeCompare(a.statement_date || ''))
  );
  const sourceNames = Object.keys(grouped).sort();

  const currentSource = activeSource && sourceNames.includes(activeSource) ? activeSource : sourceNames[0] || null;
  const currentStmts = currentSource ? (grouped[currentSource] || []) : [];
  const completedCount = currentStmts.filter(s => s.status === 'completed').length;
  const totalPages = Math.max(1, Math.ceil(currentStmts.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pagedStmts = currentStmts.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  if (loading) return (
    <div className="p-8 flex items-center justify-center h-full">
      <div className="text-[#5C5C55] text-sm">Loading business upload history...</div>
    </div>
  );

  return (
    <div className="p-6 lg:p-8 space-y-5 max-w-[1200px]" data-testid="business-upload-history-page">
      <div>
        <h2 className="font-heading text-2xl sm:text-3xl tracking-tight text-[#1C1C19]">Business Upload History</h2>
        <p className="text-sm text-[#5C5C55] mt-1">All uploaded business statements organized by source.</p>
      </div>

      {/* Top-level tabs: Bank Accounts | Credit Cards */}
      <div className="flex items-center gap-1 bg-[#E8E6DE] p-1 rounded-lg w-fit">
        <button data-testid="biz-history-tab-banks"
          onClick={() => { setTopTab('bank'); setActiveSource(null); setPage(1); }}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
            isBankTab ? 'bg-white text-[#1C1C19] shadow-sm' : 'text-[#5C5C55] hover:text-[#1C1C19]'
          }`}>
          <Building2 size={15} /> Bank Accounts
          <span className="text-[10px] bg-[#E8E6DE] text-[#5C5C55] px-1.5 py-0.5 rounded-full font-mono">{bankStatements.length}</span>
        </button>
        <button data-testid="biz-history-tab-cards"
          onClick={() => { setTopTab('cards'); setActiveSource(null); setPage(1); }}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
            !isBankTab ? 'bg-white text-[#1C1C19] shadow-sm' : 'text-[#5C5C55] hover:text-[#1C1C19]'
          }`}>
          <CreditCard size={15} /> Credit Cards
          <span className="text-[10px] bg-[#E8E6DE] text-[#5C5C55] px-1.5 py-0.5 rounded-full font-mono">{cardStatements.length}</span>
        </button>
      </div>

      {sourceNames.length > 0 ? (
        <>
          {/* Source sub-tabs */}
          <div className="flex items-center gap-1 border-b border-[#E2E0D8] overflow-x-auto" data-testid="biz-source-tabs">
            {sourceNames.map(src => {
              const count = grouped[src].length;
              const isActive = src === currentSource;
              return (
                <button key={src} data-testid={`biz-source-tab-${src.replace(/\s+/g, '-')}`}
                  onClick={() => { setActiveSource(src); setPage(1); }}
                  className={`relative flex items-center gap-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors ${
                    isActive ? 'text-[#284A33]' : 'text-[#5C5C55] hover:text-[#1C1C19]'
                  }`}>
                  {isBankTab ? <Building2 size={13} /> : <CreditCard size={13} />}
                  {src}
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-mono ${
                    isActive ? 'bg-[#284A33]/10 text-[#284A33]' : 'bg-[#E8E6DE] text-[#5C5C55]'
                  }`}>{count}</span>
                  {isActive && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#284A33] rounded-t" />}
                </button>
              );
            })}
          </div>

          {/* Active source statement table */}
          {currentSource && currentStmts.length > 0 && (
            <div className="bg-white border border-[#E2E0D8] rounded-lg overflow-hidden">
              <div className="px-5 py-3 border-b border-[#E2E0D8] flex items-center justify-between bg-[#F7F6F2]">
                <div className="flex items-center gap-2">
                  {isBankTab ? <Building2 size={14} className="text-[#284A33]" /> : <CreditCard size={14} className="text-[#284A33]" />}
                  <span className="text-sm font-medium text-[#1C1C19]">{currentSource}</span>
                </div>
                <span className="text-xs font-mono text-[#5C5C55]">{completedCount}/{currentStmts.length} completed</span>
              </div>

              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#E2E0D8]">
                    <th className="text-left px-5 py-2 text-[10px] font-semibold text-[#5C5C55] uppercase tracking-wider">Month</th>
                    <th className="text-left px-4 py-2 text-[10px] font-semibold text-[#5C5C55] uppercase tracking-wider">File</th>
                    <th className="text-left px-4 py-2 text-[10px] font-semibold text-[#5C5C55] uppercase tracking-wider">Status</th>
                    <th className="text-right px-4 py-2 text-[10px] font-semibold text-[#5C5C55] uppercase tracking-wider">Transactions</th>
                    <th className="text-left px-4 py-2 text-[10px] font-semibold text-[#5C5C55] uppercase tracking-wider">Uploaded</th>
                    <th className="text-right px-5 py-2 text-[10px] font-semibold text-[#5C5C55] uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E2E0D8]">
                  {pagedStmts.map(stmt => {
                    const monthLabel = (() => {
                      const src = stmt.month || stmt.statement_date;
                      if (!src) return '-';
                      const [y, mo] = src.split('-');
                      return `${MONTH_SHORT[parseInt(mo) - 1]} ${y}`;
                    })();
                    return (
                      <tr key={stmt.id} className="hover:bg-[#FAFAF8] transition-colors">
                        <td className="px-5 py-2.5 text-sm font-medium text-[#1C1C19]">{monthLabel}</td>
                        <td className="px-4 py-2.5">
                          <span className="text-xs text-[#5C5C55] truncate max-w-[200px] block" title={stmt.original_filename || stmt.filename}>
                            {stmt.original_filename || stmt.filename || '-'}
                          </span>
                        </td>
                        <td className="px-4 py-2.5"><StatusBadge status={stmt.status} /></td>
                        <td className="px-4 py-2.5 text-right text-xs font-mono text-[#5C5C55]">
                          {stmt.transaction_count || 0}
                          {stmt.junk_count > 0 && <span className="text-orange-500 ml-1">({stmt.junk_count} junk)</span>}
                        </td>
                        <td className="px-4 py-2.5 text-xs text-[#5C5C55]">{fmtDate(stmt.created_at)}</td>
                        <td className="px-5 py-2.5 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button onClick={() => setDeleteDialog({ open: true, id: stmt.id, name: `${currentSource} ${monthLabel}` })}
                              className="text-[#B5B3A9] hover:text-red-500 transition-colors" title="Delete statement"
                              data-testid={`biz-delete-stmt-${stmt.id}`}>
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="px-5 py-3 border-t border-[#E2E0D8] flex items-center justify-between bg-[#FAFAF8]">
                  <span className="text-xs text-[#5C5C55] font-mono">
                    {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, currentStmts.length)} of {currentStmts.length}
                  </span>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                      <button key={p} data-testid={`biz-page-${p}`}
                        onClick={() => setPage(p)}
                        className={`w-8 h-8 rounded-md text-xs font-medium transition-all ${
                          p === safePage
                            ? 'bg-[#284A33] text-white'
                            : 'text-[#5C5C55] hover:bg-[#E8E6DE]'
                        }`}>
                        {p}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="bg-white border border-[#E2E0D8] rounded-lg p-10 text-center">
          <FileText size={40} className="text-[#D5D2C6] mx-auto mb-3" />
          <p className="text-sm text-[#5C5C55]">No business {isBankTab ? 'bank' : 'credit card'} statements uploaded yet.</p>
          <p className="text-xs text-[#8A8880] mt-1">Upload statements from the Business Upload page to see history here.</p>
        </div>
      )}

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(o) => !o && setDeleteDialog({ open: false, id: null, name: '' })}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Business Statement</DialogTitle>
            <DialogDescription>Delete "{deleteDialog.name}" and all its linked transactions? This cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteDialog({ open: false, id: null, name: '' })}>Cancel</Button>
            <Button variant="destructive" onClick={() => handleDelete(deleteDialog.id)}
              data-testid="biz-confirm-delete-stmt" className="bg-red-600 hover:bg-red-700">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
